import moment from "moment";
import { getNettoWorkTime, timeDifference } from "my-time/utils";
import {
  exceedsDailyWorkTimeLimitError,
  exceedsStandByIntervallError,
  exceedsWeeklyWorktimeLimitError,
  overlappingTimePeriodError,
  timePeriodNotAppropriateError,
  personalLeaveRequiresWorkingTimeError,
} from "my-time/utils/errors";

type MyTimeRequest = {
  myTimeType: string;
  timeDataTypeCode?: string;
  startTime: string | Date;
  endTime: string | Date;
  hours?: number;
  date?: string | Date;
  workBreakInMinutes?: number;
  dayType?: string;
};

type ValidateNewRequestParams = {
  requests: MyTimeRequest[];
  list: MyTimeRequest[];
  data: MyTimeRequest;
  protectedTimeRanges: MyTimeRequest[];
  timePeriod: number;
  weeklyWorktimeLimit: number;
  weeklyVoluntaryOvertimeLimit: number;
  error: any;
  setError: (err: any) => void;
  startTime: string | Date;
  endTime: string | Date;
};

export const validateNewRequest = ({
  requests,
  list,
  data,
  protectedTimeRanges,
  timePeriod,
  weeklyWorktimeLimit,
  weeklyVoluntaryOvertimeLimit,
  error,
  setError,
  startTime,
  endTime,
}: ValidateNewRequestParams): boolean => {
  const DAILY_WORK_TIME_LIMIT_IN_MINUTES = 12 * 60;

  // Check if startTime and endTime are the same (0 hours) or negative duration
  const startMoment = moment(startTime);
  const endMoment = moment(endTime);

  if (startMoment.isSame(endMoment)) {
    setError({
      state: true,
      messages: ["MY_TIME_ZERO_DURATION_NOT_ALLOWED"],
    });
    return true; // Return true to indicate error
  }

  // Check if endTime is before startTime (negative duration)
  if (endMoment.isBefore(startMoment)) {
    setError({
      state: true,
      messages: ["MY_TIME_NEGATIVE_DURATION_NOT_ALLOWED"],
    });
    return true; // Return true to indicate error
  }

  let requestsWorkTime = 0;
  let requestsVoluntaryOvertime = 0;
  let dailyWorkTime = 0;
  let weeklyWorkTime = 0;
  let weeklyVoluntaryOvertime = 0;

  const dailyList = list.filter(
    (item) =>
      moment(item.date).format("YYYY-MM-DD") ===
      moment(data?.date).format("YYYY-MM-DD"),
  );

  const isOverlapped = protectedTimeRanges.find((protectedItem) => {
    const startMoment = moment(startTime);
    const endMoment = moment(endTime);
    const protectedStartMoment = moment(protectedItem.startTime);
    const protectedEndMoment = moment(protectedItem.endTime);

    if (
      data.myTimeType === "STAND_BY_CALL" &&
      protectedItem.myTimeType !== "WORKING_TIME"
    ) {
      return false;
    }

    // PERSONAL_LEAVE is allowed within WORKING_TIME (bidirectional exemption)
    if (
      (data.myTimeType === "PERSONAL_LEAVE" && protectedItem.myTimeType === "WORKING_TIME") ||
      (data.myTimeType === "WORKING_TIME" && protectedItem.myTimeType === "PERSONAL_LEAVE")
    ) {
      return false;
    }

    // Check for any time overlap
    const overlaps =
      (startMoment >= protectedStartMoment && startMoment < protectedEndMoment) ||
      (endMoment > protectedStartMoment && endMoment <= protectedEndMoment) ||
      (startMoment <= protectedStartMoment && endMoment >= protectedEndMoment);

    // For OVERTIME, check overlap with other OVERTIME entries as well
    if (data.myTimeType === "OVERTIME" && protectedItem.myTimeType === "OVERTIME") {
      return overlaps;
    }

    return overlaps && data.myTimeType !== protectedItem.myTimeType;
  });

  if (isOverlapped) {
    overlappingTimePeriodError({ error, setError });
    return true; // Return true to indicate error
  }

  // Check for PERSONAL_LEAVE overlapping with another PERSONAL_LEAVE
  if (data.myTimeType === "PERSONAL_LEAVE") {
    const personalLeaveOverlap = protectedTimeRanges.find((protectedItem) => {
      if (protectedItem.myTimeType !== "PERSONAL_LEAVE") {
        return false;
      }

      const startMoment = moment(startTime);
      const endMoment = moment(endTime);
      const protectedStartMoment = moment(protectedItem.startTime);
      const protectedEndMoment = moment(protectedItem.endTime);

      return startMoment < protectedEndMoment && endMoment > protectedStartMoment;
    });

    if (personalLeaveOverlap) {
      overlappingTimePeriodError({ error, setError });
      return true; // Return true to indicate error
    }
  }

  if (data.myTimeType === "STAND_BY_CALL") {
    const isWithinStandBy = protectedTimeRanges.some((protectedItem) => {
      const startMoment = moment(startTime);
      const endMoment = moment(endTime);
      const protectedStartMoment = moment(protectedItem.startTime);
      const protectedEndMoment = moment(protectedItem.endTime);

      return (
        protectedItem.myTimeType === "STAND_BY" &&
        startMoment >= protectedStartMoment &&
        endMoment <= protectedEndMoment
      );
    });

    if (!isWithinStandBy) {
      exceedsStandByIntervallError({ error, setError });
      return true; // Return true to indicate error
    }
  }

  if (data.myTimeType === "PERSONAL_LEAVE") {
    // Personal leave is not allowed on weekends/rest days
    if (data.dayType === "REST_DAY") {
      personalLeaveRequiresWorkingTimeError({ error, setError });
      return true; // Return true to indicate error
    }

    const isWithinWorkingTime = protectedTimeRanges.some((protectedItem) => {
      const startMoment = moment(startTime);
      const endMoment = moment(endTime);
      const protectedStartMoment = moment(protectedItem.startTime);
      const protectedEndMoment = moment(protectedItem.endTime);

      return (
        protectedItem.myTimeType === "WORKING_TIME" &&
        startMoment >= protectedStartMoment &&
        endMoment <= protectedEndMoment
      );
    });

    if (!isWithinWorkingTime) {
      personalLeaveRequiresWorkingTimeError({ error, setError });
      return true; // Return true to indicate error
    }
  }

  dailyList.forEach((item) => {
    const startTime = moment(item.startTime).toDate();
    const endTime = moment(item.endTime).toDate();
    const workTime =
      timeDifference({ startTime, endTime }) - (item.workBreakInMinutes ?? 0);
    if (
      item.myTimeType === "STAND_BY" ||
      item.myTimeType === "LEAVE" ||
      item.myTimeType === "ABSENCE" ||
      item.myTimeType === "ON_CALL" ||
      item.myTimeType === "PERSONAL_LEAVE"
    ) {
      return;
    } else {
      if (item.myTimeType === "WORKING_TIME") {
        const isExist = requests.find(
          (request) => request.myTimeType === "WORKING_TIME",
        );
        if (!isExist) {
          dailyWorkTime = dailyWorkTime + workTime;
        }
      } else {
        dailyWorkTime = dailyWorkTime + workTime;
      }

      if (item.timeDataTypeCode === "Tö") {
        weeklyVoluntaryOvertime = weeklyVoluntaryOvertime + workTime;
      }
    }
  });

  requests.forEach((item) => {
    const nettoWorkTime = getNettoWorkTime(
      item.hours !== undefined ? item.hours.toString() : "0",
    );
    const startTime = moment(item.startTime).toDate();
    const endTime = moment(item.endTime).toDate();
    const workTime = nettoWorkTime;

    // PERSONAL_LEAVE has deduction value, don't count towards daily limit
    if (item.myTimeType !== "PERSONAL_LEAVE") {
      requestsWorkTime = requestsWorkTime + workTime;
    }

    if (item.timeDataTypeCode === "Tö") {
      weeklyVoluntaryOvertime = weeklyVoluntaryOvertime + workTime;
      requestsVoluntaryOvertime = requestsVoluntaryOvertime + workTime;
    }
    //const isOverlappedRequests = protectedTimeRanges.find(
    // (item) =>
    //(moment(startTime) > moment(item.startTime) &&
    //moment(startTime) < moment(item.endTime)) ||
    //(moment(endTime) < moment(item.endTime) &&
    //moment(endTime) > moment(item.startTime))
    //);
    //if (isOverlappedRequests) {
    //if (item?.myTimeType !== "WORKING_TIME") {
    //overlappingTimePeriodError({ error, setError });
    //}
    //}
    const startTimeMinutesPart = moment(startTime).minutes();
    const endTimeMinutesPart = moment(endTime).minutes();
    if (timePeriod > 0) {
      const isStartTimeAppropriate = startTimeMinutesPart % timePeriod;
      const isEndTimeAppropriate = endTimeMinutesPart % timePeriod;
      if (isStartTimeAppropriate !== 0 || isEndTimeAppropriate !== 0) {
        timePeriodNotAppropriateError({ error, setError });
        return true; // Return true to indicate error
      }
    }
  });

  let effectiveDailyWorkTime = dailyWorkTime + requestsWorkTime;
  if (effectiveDailyWorkTime > DAILY_WORK_TIME_LIMIT_IN_MINUTES) {
    exceedsDailyWorkTimeLimitError({ error, setError });
    return true; // Return true to indicate error
  }

  const weeklyList = list.filter(
    (item) =>
      moment(item.date).format("YYYY-MM-DD") >=
      moment(data?.date).startOf("week").format("YYYY-MM-DD") &&
      moment(item.date).format("YYYY-MM-DD") <=
      moment(data?.date).endOf("week").format("YYYY-MM-DD"),
  );
  weeklyList.forEach((item) => {
    if (
      item.myTimeType === "STAND_BY" ||
      item.myTimeType === "LEAVE" ||
      item.myTimeType === "ABSENCE" ||
      item.myTimeType === "ON_CALL" ||
      item.myTimeType === "PERSONAL_LEAVE"
    )
      return;
    if (item.myTimeType) {
      const startTime = moment(item.startTime).toDate();
      const endTime = moment(item.endTime).toDate();
      let workTime = 0;
      if (item.hours) {
        workTime = getNettoWorkTime(item.hours.toString());
      } else {
        workTime =
          timeDifference({ startTime, endTime }) -
          (item.workBreakInMinutes ?? 0);
      }
      const isSame = requests.find((request) => {
        if (
          request.myTimeType === item.myTimeType &&
          moment(request.startTime).format("YYYY-MM-DD") ===
          moment(item.date).format("YYYY-MM-DD")
        ) {
          return true;
        }
        return false;
      });
      if (item.timeDataTypeCode === "Tö") {
        weeklyVoluntaryOvertime = weeklyVoluntaryOvertime + workTime;
      } else {
        if (!isSame) {
          weeklyWorkTime = weeklyWorkTime + workTime;
        }
      }
    }
  });

  const dataTime = timeDifference({
    startTime: moment(startTime).toDate(),
    endTime: moment(endTime).toDate(),
  });
  const cumulatedWorkTimeLimit =
    weeklyWorktimeLimit + weeklyVoluntaryOvertimeLimit;

  // PERSONAL_LEAVE has deduction value, don't count towards weekly limit
  const cumulatedWorkTime = data.myTimeType === "PERSONAL_LEAVE"
    ? weeklyWorkTime
    : weeklyWorkTime + dataTime;

  if (cumulatedWorkTime > cumulatedWorkTimeLimit) {
    exceedsWeeklyWorktimeLimitError({ error, setError });
    return true; // Return true to indicate error
  }

  return false; // No errors
};

export default validateNewRequest;
